/**
 * TasteCard
 *
 * Collapsible bio card with headline tagline, trait chips, and "Read more" expansion.
 * Uses glassmorphism styling and auto-extracts traits from TasteInsights.
 */
import { useState, useMemo } from 'react'
import {
  Box,
  Text,
  VStack,
  HStack,
  Wrap,
  WrapItem,
  Tag,
  Collapse,
  Button,
  Icon,
} from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, ChevronUp, MapPin } from 'lucide-react'
import { haptics } from '@/utils/haptics'
import { FavoriteCuisine, getDominantCuisineColor } from '@/utils/cuisineColorMapping'

const MotionBox = motion.create(Box)

export interface TasteCardProps {
  /** User's bio */
  bio?: string | null
  /** User's tagline/headline from TasteInsights */
  tagline?: string | null
  /** User's taste tribe label */
  tasteTribe?: string | null
  /** Emojis for taste tribe */
  tasteTribeEmojis?: string | null
  /** Dining styles from TasteInsights */
  diningStyles?: string[]
  /** Key flavors from TasteInsights */
  keyFlavors?: string[]
  /** Global wheelhouse cuisines from TasteInsights */
  globalWheelhouse?: string[]
  /** User's favorite cuisines for accent colors */
  favoriteCuisines?: FavoriteCuisine[]
  /** User's location */
  location?: string | null
  /** Whether to start expanded */
  defaultExpanded?: boolean
  /** Maximum bio length before truncation */
  maxBioLength?: number
}

export function TasteCard({
  bio,
  tagline,
  tasteTribe,
  tasteTribeEmojis,
  diningStyles = [],
  keyFlavors = [],
  globalWheelhouse = [],
  favoriteCuisines = [],
  location,
  defaultExpanded = false,
  maxBioLength = 120,
}: TasteCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  // Determine accent color from cuisines
  const accentColor = getDominantCuisineColor(favoriteCuisines)

  // Check if bio needs truncation
  const needsTruncation = (bio?.length || 0) > maxBioLength
  const truncatedBio = needsTruncation ? `${bio?.slice(0, maxBioLength)}...` : bio

  // Combine traits for display (limit to prevent overflow)
  const allTraits = useMemo(() => {
    const traits: Array<{ label: string; type: 'dining' | 'flavor' | 'cuisine' }> = []

    // Add dining styles (max 2)
    diningStyles.slice(0, 2).forEach((style) => {
      traits.push({ label: style, type: 'dining' })
    })

    // Add key flavors (max 2)
    keyFlavors.slice(0, 2).forEach((flavor) => {
      traits.push({ label: flavor, type: 'flavor' })
    })

    // Add global wheelhouse cuisines (max 2)
    globalWheelhouse.slice(0, 2).forEach((cuisine) => {
      traits.push({ label: cuisine, type: 'cuisine' })
    })

    return traits
  }, [diningStyles, keyFlavors, globalWheelhouse])

  const handleToggle = async () => {
    await haptics.selection()
    setIsExpanded(!isExpanded)
  }

  return (
    <MotionBox
      bg="var(--card-bg)"
      borderRadius="var(--r-card-compact)"
      border="var(--card-border)"
      boxShadow="var(--card-shadow)"
      p={4}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <VStack align="stretch" spacing={3}>
        {/* Taste Tribe Badge */}
        {tasteTribe && (
          <HStack spacing={2}>
            {tasteTribeEmojis && (
              <Text fontSize="lg">{tasteTribeEmojis}</Text>
            )}
            <Text
              fontSize="sm"
              fontWeight="semibold"
              color="var(--card-text)"
              textTransform="uppercase"
              letterSpacing="wider"
            >
              {tasteTribe}
            </Text>
          </HStack>
        )}

        {/* Tagline */}
        {tagline && (
          <Text
            fontSize={{ base: 'md', md: 'lg' }}
            color="var(--card-text)"
            fontStyle="italic"
            lineHeight="tall"
          >
            "{tagline}"
          </Text>
        )}

        {/* Bio */}
        {bio && (
          <Box>
            <Text
              fontSize="sm"
              color="var(--card-text-2)"
              lineHeight="tall"
            >
              {isExpanded ? bio : truncatedBio}
            </Text>

            {needsTruncation && (
              <Button
                variant="link"
                size="sm"
                color={accentColor}
                onClick={handleToggle}
                mt={1}
                rightIcon={
                  <Icon as={isExpanded ? ChevronUp : ChevronDown} boxSize={4} />
                }
              >
                {isExpanded ? 'Show less' : 'Read more'}
              </Button>
            )}
          </Box>
        )}

        {/* Trait Chips */}
        <AnimatePresence>
          {allTraits.length > 0 && (
            <Collapse in={!isExpanded || allTraits.length > 0} animateOpacity>
              <Wrap spacing={2} pt={1}>
                {allTraits.map((trait, index) => (
                  <WrapItem key={`${trait.type}-${trait.label}`}>
                    <MotionBox
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <TraitChip
                        label={trait.label}
                        type={trait.type}
                        accentColor={accentColor}
                      />
                    </MotionBox>
                  </WrapItem>
                ))}
              </Wrap>
            </Collapse>
          )}
        </AnimatePresence>

        {/* Location (expanded) */}
        {location && (
          <Collapse in={isExpanded} animateOpacity>
            <HStack spacing={2} color="var(--card-text-3)" pt={2}>
              <Icon as={MapPin} boxSize={4} />
              <Text fontSize="sm">{location}</Text>
            </HStack>
          </Collapse>
        )}
      </VStack>
    </MotionBox>
  )
}

/**
 * Individual trait chip with subtle color coding by type
 */
function TraitChip({
  label,
  type,
  accentColor,
}: {
  label: string
  type: 'dining' | 'flavor' | 'cuisine'
  accentColor: string
}) {
  // Subtle color variations by type
  const colorMap = {
    dining: 'purple.400',
    flavor: 'orange.400',
    cuisine: 'teal.400',
  }

  return (
    <Tag
      size="sm"
      variant="subtle"
      bg="var(--card-surface-2)"
      color="var(--card-text)"
      borderRadius="full"
      px={3}
      py={1}
      fontSize="xs"
      fontWeight="medium"
      borderWidth="1px"
      borderColor="var(--card-divider)"
      _hover={{
        bg: 'var(--card-divider)',
        borderColor: colorMap[type],
      }}
      transition="all 0.2s"
    >
      {label}
    </Tag>
  )
}

/**
 * Minimal version for compact displays
 */
export function TasteCardCompact({
  tasteTribe,
  tasteTribeEmojis,
  tagline,
}: Pick<TasteCardProps, 'tasteTribe' | 'tasteTribeEmojis' | 'tagline'>) {
  return (
    <HStack
      bg="var(--card-surface-2)"
      borderRadius="var(--r-chip)"
      px={3}
      py={2}
      spacing={2}
    >
      {tasteTribeEmojis && <Text>{tasteTribeEmojis}</Text>}
      <Text fontSize="sm" color="var(--card-text)" fontWeight="medium" noOfLines={1}>
        {tasteTribe || tagline || 'Discovering their taste...'}
      </Text>
    </HStack>
  )
}
